import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Target, Users, TrendingUp } from "lucide-react";

const AboutPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const expertise = [
    { icon: TrendingUp, label: "Cashflow & liquiditeit", years: "20+" },
    { icon: Target, label: "Proces & doorstroom", years: "15+" },
    { icon: Users, label: "Eigenaarschap & teams", years: "12+" },
    { icon: Award, label: "Implementatie & verandering", years: "10+" },
  ];

  const values = [
    {
      title: "Resultaatgericht",
      description: "Elke interventie is gericht op concrete verbetering in cashflow en doorstroom.",
    },
    {
      title: "Strategisch partnership",
      description: "Niet als externe adviseur, maar als verlengstuk van het leiderschapsteam.",
    },
    {
      title: "Eerlijke diagnose",
      description: "Geen oppervlakkige optimalisatie, maar inzicht in waar het daadwerkelijk misgaat.",
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-mist relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.span {...fadeUp} className="caption text-cta mb-4 block">
                Over Rabia
              </motion.span>
              <motion.h1 
                {...fadeUp}
                transition={{ delay: 0.1 }}
                className="heading-1 mb-6"
                data-testid="about-headline"
              >
                Rabia Boutkabout
              </motion.h1>
              <motion.p 
                {...fadeUp}
                transition={{ delay: 0.2 }}
                className="text-xl md:text-2xl font-serif text-berry mb-6"
              >
                Grondlegger van de Regie op Cashflow-aanpak
              </motion.p>
              <motion.p 
                {...fadeUp}
                transition={{ delay: 0.3 }}
                className="body-large mb-4"
              >
                Veel organisaties groeien in omzet, maar verliezen grip op wat er daarna gebeurt.
              </motion.p>
              <motion.p 
                {...fadeUp}
                transition={{ delay: 0.35 }}
                className="text-navy/70 mb-8"
              >
                Op basis van meer dan twintig jaar ervaring ontwikkelde Rabia een aanpak waarin operatie, cashflow en eigenaarschap samenkomen zodat omzet daadwerkelijk doorstroomt naar cash.
              </motion.p>
              <motion.div {...fadeUp} transition={{ delay: 0.4 }}>
                <a
                  href="https://calendly.com/rabia-regieopcashflow/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Plan een gesprek
                  <ArrowRight size={18} />
                </a>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <img
                src="/rabia-about-optimized.jpg"
                alt="Rabia Boutkabout"
                className="w-full aspect-[3/4] object-cover object-top shadow-2xl"
                data-testid="about-portrait"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="heading-2 mb-8 lg:whitespace-nowrap">
              Waar de aanpak het verschil maakt
            </motion.h2>
            
            <div className="space-y-6 text-navy/70 text-lg leading-relaxed">
              <motion.p {...fadeUp} transition={{ delay: 0.2 }}>
                Veel organisaties groeien in omzet, maar merken dat cash achterblijft.
              </motion.p>
              <motion.p {...fadeUp} transition={{ delay: 0.25 }}>
                Het werk wordt gedaan, projecten lopen en klanten worden bediend,
                maar ergens in de keten vertraagt de doorstroom tussen operatie, finance en betaling.
              </motion.p>
              <motion.p {...fadeUp} transition={{ delay: 0.3 }}>
                De oorzaak ligt zelden bij één afdeling.
                Het ontstaat in de samenhang tussen processen, sturing en eigenaarschap.
              </motion.p>
              <motion.p {...fadeUp} transition={{ delay: 0.35 }}>
                De Regie op Cashflow-aanpak richt zich op drie elementen:
              </motion.p>
              <motion.ul {...fadeUp} transition={{ delay: 0.4 }} className="space-y-2 pl-4">
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-cta rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-navy">Cashflow</strong> — inzicht in cijfers en doorstroom</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-cta rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-navy">Structuur</strong> — hoe werk door de organisatie beweegt</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 bg-cta rounded-full mt-2.5 flex-shrink-0" />
                  <span><strong className="text-navy">Eigenaarschap</strong> — wie stuurt en verantwoordelijk is</span>
                </li>
              </motion.ul>
              <motion.p {...fadeUp} transition={{ delay: 0.45 }}>
                Door deze elementen opnieuw op elkaar in te richten, ontstaat een organisatie waarin cashflow geen toeval meer is.
              </motion.p>
              <motion.p {...fadeUp} transition={{ delay: 0.5 }} className="text-berry font-medium">
                Rabia begeleidt dit proces van analyse tot implementatie en borging.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="section-padding bg-mist">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.span {...fadeUp} className="caption text-cta mb-4 block">
              Expertise
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="heading-2">
              Gebouwd op ervaring in de praktijk
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, idx) => (
              <motion.div
                key={item.label}
                {...fadeUp}
                transition={{ delay: 0.1 * idx }}
                className="bg-white p-8 text-center hover:shadow-lg transition-shadow duration-300"
                data-testid={`expertise-card-${idx}`}
              >
                <item.icon className="w-10 h-10 text-cta mx-auto mb-4" strokeWidth={1} />
                <span className="font-serif text-3xl text-berry block mb-2">{item.years}</span>
                <span className="text-sm text-navy/70">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-burgundy text-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.span {...fadeUp} className="text-cta text-xs tracking-widest uppercase mb-4 block">
              Werkwijze
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-white">
              Hoe Rabia werkt
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={value.title}
                {...fadeUp}
                transition={{ delay: 0.1 * idx }}
                className="p-8 bg-white/5 border border-white/10 rounded-sm"
                data-testid={`value-card-${idx}`}
              >
                <div className="w-12 h-1 bg-cta mb-6" />
                <h3 className="font-serif text-2xl text-white mb-4">{value.title}</h3>
                <p className="text-white/70 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span {...fadeUp} className="caption text-cta mb-4 block">
              Samenwerken
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="heading-2 mb-6">
              Klaar om grip te krijgen op je cashflow?
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="body-large mb-10">
              In een gesprek wordt duidelijk waar jouw organisatie vertraagt<br />
              en welke stap nodig is om regie terug te brengen.
            </motion.p>
            <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
              <a
                href="https://calendly.com/rabia-regieopcashflow/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-final-cta"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                Plan een gesprek
                <ArrowRight size={18} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
